// @ai-generated 2026-08-28T07:59:23+00:00 by matthias.gessinger@synqony.com using Claude Opus 5 (1M context)
import { getDynamicExportTypeLabel } from '@Constants/dynamicExportConstants';
import { type ApiDynamicExportRun } from '@Model';
import { Button, EnumMessageLevel, useToasts } from '@synqo/frontend-component-library';
import { IconCopy } from '@tabler/icons-react';
import { useCallback, useMemo } from 'react';
import { FileDownloadCell } from '../Cells/FileDownloadCell';
import { buildExportRunReport, describeExportRunOutcome } from './exportRunOutcome';
import { buildExportRunTimeline } from './exportRunTimeline';
import { RunDetailFact, RunDetailLayout } from './RunDetailLayout';

interface ExportRunDetailProps {
    run: ApiDynamicExportRun;
}

/**
 * One export run, read the way a user asks about it: did it work, how far did it get, and what did
 * it have to say. The verdict and the timeline answer the first two from the run's state and its two
 * timestamps; the message list answers the third and is the reason this is a panel rather than an
 * expanded table row — a schema check reports far more than a row can hold.
 */
export function ExportRunDetail({ run }: ExportRunDetailProps) {
    const { pushToast } = useToasts();

    const timeline = useMemo(() => buildExportRunTimeline(run), [run]);
    const outcome = useMemo(() => describeExportRunOutcome(run), [run]);

    const copyReport = useCallback(() => {
        const written = navigator.clipboard?.writeText(buildExportRunReport(run));
        if (!written) {
            pushToast({
                title: 'Meldungen',
                message: 'Kopieren ist hier nicht möglich.',
                level: EnumMessageLevel.Danger,
            });
            return;
        }
        void written.then(
            () =>
                pushToast({
                    title: 'Meldungen',
                    message: 'Der Bericht liegt in der Zwischenablage.',
                    level: EnumMessageLevel.Success,
                }),
            () =>
                pushToast({
                    title: 'Meldungen',
                    message: 'Der Bericht konnte nicht kopiert werden.',
                    level: EnumMessageLevel.Danger,
                }),
        );
    }, [run, pushToast]);

    return (
        <RunDetailLayout
            variant={outcome.variant}
            headline={outcome.headline}
            detail={outcome.detail}
            action={
                run.result_file_id && <FileDownloadCell fileId={run.result_file_id} label="Exportdatei herunterladen" />
            }
            timeline={timeline}
            timelineLabel="Exportstatus"
            facts={
                <>
                    <RunDetailFact label="Export" value={getDynamicExportTypeLabel(run.category, run.key)} />
                    <RunDetailFact label="Lauf-Nr." value={run.number} />
                    <RunDetailFact label="Ausgelöst von" value={run.created_by} />
                </>
            }
            result={run}
            noMessages="Dieser Lauf hat keine Meldungen hinterlassen."
            messagesAction={
                <Button
                    appearance="transparent"
                    size="xs"
                    onClick={copyReport}
                    label="Bericht in die Zwischenablage kopieren"
                >
                    <IconCopy size={14} />
                    &nbsp;Kopieren
                </Button>
            }
        />
    );
}
